import { useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { Download, FileArchive, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { IMPORT_MUTATION_KEY, useExportData } from "@/hooks/use-data";
import { describeCallableError, type ExportDataResult } from "@/lib/callables";
import type { StepState } from "@/lib/dataTransfer";
import { formatDateTime } from "@/lib/format";
import { BackupSummaryView } from "./BackupSummaryView";
import { Step } from "./Step";

/**
 * A exportação em dois passos: o servidor gera o ZIP no Storage (com muitos
 * arquivos, leva minutos) e o administrador baixa pelo link. O download é um
 * clique de quem usa, e não automático: o link é de outro domínio, o do
 * Storage, e aberto numa aba nova ele não leva embora a página.
 */
export function ExportCard() {
  const exporter = useExportData();
  const importing = useIsMutating({ mutationKey: IMPORT_MUTATION_KEY }) > 0;
  const [result, setResult] = useState<ExportDataResult | null>(null);
  const [downloaded, setDownloaded] = useState(false);

  async function generate() {
    setResult(null);
    setDownloaded(false);
    try {
      setResult(await exporter.mutateAsync());
      toast.success("Arquivo gerado. Falta baixar.");
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  const generateState: StepState = exporter.isPending
    ? "running"
    : result
      ? "done"
      : exporter.isError
        ? "failed"
        : "pending";

  return (
    <Card>
      <CardHeader>
        <CardTitle>Exportar</CardTitle>
        <CardDescription>
          O Firestore inteiro e todos os arquivos do Storage, juntos num ZIP. Numa importação, cada
          arquivo volta no mesmo caminho e com o mesmo token, e as URLs gravadas no Firestore
          continuam abrindo as mesmas imagens.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-caption text-muted-foreground">
          Ficam de fora a coleção <span className="font-mono">_meta</span> (quem acessa o painel),
          as contas de login e, no Storage, as pastas <span className="font-mono">backups/</span> e{" "}
          <span className="font-mono">imports/</span>, onde moram as próprias exportações.
        </p>

        <Step number={1} title="Gerar o arquivo" state={generateState}>
          <div>
            <Button onClick={() => void generate()} disabled={exporter.isPending || importing}>
              {exporter.isPending ? <Loader2 className="animate-spin" /> : <FileArchive />}
              {result ? "Gerar de novo" : "Gerar o ZIP"}
            </Button>
          </div>
          {exporter.isPending ? (
            <p className="text-caption text-muted-foreground">
              O servidor está juntando o Firestore e o Storage. Com muitos arquivos, leva alguns
              minutos; não feche esta aba.
            </p>
          ) : null}
          {importing ? (
            <p className="text-caption text-muted-foreground">
              Espere a importação em andamento terminar para exportar.
            </p>
          ) : null}
          {result ? (
            <BackupSummaryView
              summary={result.summary}
              fileName={result.fileName}
              zipBytes={result.zipBytes}
            />
          ) : null}
        </Step>

        <Step number={2} title="Baixar o arquivo" state={downloaded ? "done" : "pending"}>
          {result ? (
            <>
              <div>
                <Button asChild variant={downloaded ? "outline" : "default"}>
                  <a
                    href={result.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setDownloaded(true)}
                  >
                    <Download /> {downloaded ? "Baixar de novo" : "Baixar o ZIP"}
                  </a>
                </Button>
              </div>
              <p className="text-caption text-muted-foreground">
                {result.downloadExpiresAt
                  ? `O link vale até ${formatDateTime(result.downloadExpiresAt)}. `
                  : ""}
                O arquivo também fica guardado no Storage, em{" "}
                <span className="break-all font-mono">{result.path}</span>.
              </p>
            </>
          ) : (
            <p className="text-caption text-muted-foreground">
              O link aparece aqui quando o arquivo estiver pronto.
            </p>
          )}
        </Step>
      </CardContent>
    </Card>
  );
}
